import React, { useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { vendorQuotationService } from "services/vendorQuotationService";
import QuotationTrailPdfButton from "./QuotationTrailPdfButton";
import RfqSubProjectsSummary from "components/RfqSubProjectsSummary";

const uppercaseMeridiem = (value) =>
  value.replace(/\b(am|pm)\b/gi, (meridiem) => meridiem.toUpperCase());

const getDisplayRfqStatus = (status) => {
  const normalizedStatus = String(status || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toUpperCase();

  if (normalizedStatus === "PENDING APPROVAL") return "In Review";
  if (!normalizedStatus) return "-";

  return normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase();
};

const getRfqStatusIndicatorColor = (status) => {
  const normalizedStatus = String(status || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toUpperCase();

  if (normalizedStatus === "OPEN") return "bg-[#16A34A]";
  if (normalizedStatus === "PENDING APPROVAL" || normalizedStatus === "IN REVIEW") {
    return "bg-[#F97316]";
  }
  if (normalizedStatus === "CLOSED") return "bg-[#DC2626]";

  return "bg-[#9CA3AF]";
};

const getRfqStatusTooltip = (status) => {
  const normalizedStatus = String(status || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toUpperCase();

  if (normalizedStatus === "OPEN") {
    return {
      label: "Open",
      message: "RFQ is open to submit or edit your quotation.",
    };
  }

  if (normalizedStatus === "PENDING APPROVAL" || normalizedStatus === "IN REVIEW") {
    return {
      label: "In Review",
      message: "Quotation cannot be edited.",
    };
  }

  if (normalizedStatus === "CLOSED") {
    return {
      label: "Closed",
      message: "RFQ has been closed.",
    };
  }

  return null;
};

const RfqInformation = ({ rfqData, togglePopup, isPopupOpen, isAccordion = false }) => {
  const [expandedStates, setExpandedStates] = useState({}); // Track expanded states for each item
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [isQuotationTrailOpen, setIsQuotationTrailOpen] = useState(true);
  const [latestVendorQuotations, setLatestVendorQuotations] = useState(null);
  const statusTooltip = getRfqStatusTooltip(rfqData?.status);
  const toggleExpanded = (index) => {
    setExpandedStates((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle specific item's state
    }));
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  function stripHtml(html) {
    if (!html) return '';

    // Create a new DOMParser
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Get the text content
    return doc.body.textContent || '';
  }

  const attachmentsData = rfqData?.selectedFilesBase
    ? rfqData.selectedFilesBase.map((item) => {
      const lastIndex = item.Name.lastIndexOf('.'); // Extract file extension
      const path = lastIndex !== -1 ? item.Name.substring(lastIndex + 1) : null;
      return { ...item, Path: path }; // Add the Path to determine file type
    })
    : [];

  // Check for multiple projects and locations
  const hasMultipleProjects = rfqData?.projectName?.includes('||');
  const projects = hasMultipleProjects ? rfqData.projectName.split('||') : [rfqData?.projectName];
  const siteLocation = rfqData?.projectSiteAddress || rfqData?.location;
  const hasMultipleLocations = siteLocation?.includes('||');
  const locations = hasMultipleLocations ? siteLocation.split('||') : [siteLocation];

  // console.log("projects", projects);
  const shouldShowDetails = !isAccordion || isAccordionOpen;
  const supplierId = rfqData?.suppliers?.[0]?.supplierId || "";
  const vendorQuotations =
    latestVendorQuotations || rfqData?.suppliers?.[0]?.vendorQuotations || {};
  const quotationSets = Array.isArray(vendorQuotations?.quotationSets)
    ? vendorQuotations.quotationSets
    : [];

  const firstQuotationSet = quotationSets[0];
  const quotationUpdateTrail = quotationSets
    .map((quotationSet, quotationIndex) => ({ quotationSet, quotationIndex }))
    .filter(
      ({ quotationSet }) => quotationSet?.submittedAt || quotationSet?.submittedBy
    );
  const requesterMobile = rfqData?.requesterMobile
    ? String(rfqData.requesterMobile).replace(/\D/g, "")
    : "";
  useEffect(() => {
    let isMounted = true;

    const fetchLatestVendorQuotations = async () => {
      if (!rfqData?.rfqId || !supplierId) {
        setLatestVendorQuotations(null);
        return;
      }
 
      try {
        const response = await vendorQuotationService.getVendorQuotation({
          rfqId: rfqData.rfqId,
          supplierId,
        });

        if (isMounted) {
          setLatestVendorQuotations(response?.vendorQuotations || null);
        }
      } catch (error) {
        if (isMounted) {
          setLatestVendorQuotations(null);
        }
        console.error("Failed to fetch latest vendor quotation trail:", error);
      }
    };

    fetchLatestVendorQuotations();

    return () => {
      isMounted = false;
    };
  }, [rfqData?.rfqId, supplierId]);

  const getQuotationSubmittedDateParts = (submittedAt) => {
    if (!submittedAt) {
      return {
        day: "-",
        date: "-",
        time: "-",
      };
    }

    const submittedDate = new Date(submittedAt);

    if (Number.isNaN(submittedDate.getTime())) {
      return {
        day: "-",
        date: "-",
        time: "-",
      };
    }

    const dayOfMonth = submittedDate.getDate();
    const ordinalSuffix =
      dayOfMonth % 100 >= 11 && dayOfMonth % 100 <= 13
        ? "th"
        : ["th", "st", "nd", "rd"][dayOfMonth % 10] || "th";

    return {
      day: submittedDate.toLocaleDateString("en-GB", { weekday: "short" }),
      date: `${dayOfMonth}${ordinalSuffix} ${submittedDate.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      })}`,
      time: uppercaseMeridiem(
        submittedDate.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      ),
    };
  };

  const toNumber = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const getQuotationAmount = (quotationSet) => {
    const amountWithoutGst =
      toNumber(quotationSet?.totalWithoutGst) +
      toNumber(quotationSet?.othersTotalWithoutGst);

    if (amountWithoutGst > 0) return amountWithoutGst;

    const amountWithGst =
      toNumber(quotationSet?.totalWithGst) +
      toNumber(quotationSet?.othersTotalWithGst);

    if (amountWithGst > 0) return amountWithGst;

    return toNumber(quotationSet?.selectedTotalWithGst);
  };

  const formatAmount = (amount) => {
    if (!amount) return "-";

    return `Rs. ${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
    }).format(amount)}`;
  };

  const formatDifference = (differenceAmount, previousQuotationAmount) => {
    if (!differenceAmount) return "--";

    const amountLabel = formatAmount(Math.abs(differenceAmount));
    const percentage =
      previousQuotationAmount > 0
        ? ` (${((Math.abs(differenceAmount) / previousQuotationAmount) * 100).toFixed(2)}%)`
        : "";

    return ` ${amountLabel}${percentage}`;
  };

  const getQuotationFormPath = () => {
    switch (rfqData?.rfqType) {
      case "material":
        return "/material-form";
      case "labour":
        return "/labour-form";
      case "contractor":
        return "/providing-form";
      default:
        return "";
    }
  };

  const openQuotationVersion = (quotationIndex) => {
    const formPath = getQuotationFormPath();

    if (!formPath || rfqData?.rfqId === undefined || rfqData?.rfqId === null) return;

    const storageKey = `rfq-quotation-view-${rfqData.rfqId}`;
    const rfqDataWithLatestQuotations = {
      ...rfqData,
      suppliers:
        rfqData?.suppliers?.map((supplier, index) =>
          index === 0 ? { ...supplier, vendorQuotations } : supplier
        ) || [],
    };

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ rfqData: rfqDataWithLatestQuotations }));
    } catch (error) {
      console.error("Failed to store quotation view data:", error);
    }

    const params = new URLSearchParams({
      rfqId: String(rfqData.rfqId),
      isEdit: "true",
      readOnly: "true",
      rfqType: rfqData?.rfqType || "",
      quotationIndex: String(quotationIndex),
    });

    if (supplierId) {
      params.set("supplierId", supplierId);
    }

    window.open(`${formPath}?${params.toString()}`, "_blank");
  };

  return (
    <div className="lg:py-[24px] lg:px-8">
      <div className="rfq-information-card bg-white border rounded-lg shadow-sm overflow-hidden">

        {/* Accordion Header */}
        {isAccordion && (
          <button
            type="button"
            onClick={() => setIsAccordionOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-b-gray-200 hover:bg-[#FBFBF7]"
          >
            <h3 className="text-base font-semibold">
              RFQ Information
              {/* <span className="text-xs"> - (Client Name:{rfqData?.userBusinessName})</span> */}
            </h3>

            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isAccordionOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}

        {shouldShowDetails && (
          <div className="p-4">
            <div className="rfq-information-grid lg:text-xs lg:grid lg:grid-cols-3 lg:gap-x-8 space-y-4 lg:space-y-0 lg:gap-y-4 bg-white">
              {/* <div className="lg:space-y-5 space-y-4"> */}
              <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal  ">Client Name: <span className="lg:hidden text-black font-bold">{rfqData?.userBusinessName}</span></p>
                <p className=" font-bold hidden lg:block">
                  {rfqData?.userBusinessName}
                </p>
              </div>

              <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal  lg:block">RFQ Name:</p>
                <p className=" font-bold  lg:block">
                  {rfqData?.name}
                </p>
              </div>

              <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal  lg:block">Created Date: </p>
                <p className=" font-bold  lg:block">
                  {new Date(rfqData?.createdAt).toLocaleDateString('en-GB')}
                </p>
              </div>
              {/* <div>
              <p className="text-[#6B7280] font-normal  lg:block">
                Created Date
              </p>
              <p className=" font-bold  lg:block">
                {new Date(rfqData?.createdAt).toLocaleDateString("en-GB")}
              </p>
            </div> */}

              {/* <div>
                                                            <p className="text-[#6B7280] font-normal">Payment Terms</p>
                                                            <p
                                                                className=" font-bold bg-[#ECECEC] lg:bg-white mt-1 p-4 lg:m-0 lg:p-0"
                                                                dangerouslySetInnerHTML={{ __html: rfqData?.paymentTerms }}
                                                            ></p>
        
                                                        </div> */}
              {/* </div> */}
              {/* <div className="lg:space-y-5 space-y-4"> */}

              {/* rfq type */}
              <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal   lg:block">
                  RFQ Type:
                </p>
                <p className=" font-bold   lg:block">
                  {capitalizeFirstLetter(rfqData?.rfqType)}
                </p>
              </div>
              {/* status */}
              <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal   lg:block">
                  RFQ Status:
                </p>
                <p className="font-bold lg:block">
                  <span className="group relative inline-flex items-center gap-2">
                    <span className="relative inline-flex h-2.5 w-2.5 align-middle">
                      <span
                        className={`relative inline-flex h-2.5 w-2.5 rounded-full ${getRfqStatusIndicatorColor(rfqData?.status)}`}
                      />
                    </span>
                    {getDisplayRfqStatus(rfqData?.status)}
                    {statusTooltip && (
                      <span
                        role="tooltip"
                        className="pointer-events-none invisible absolute bottom-full left-0 z-50 mb-2 w-80 rounded-lg bg-black px-4 py-3 text-left text-white opacity-0 shadow-xl transition-opacity duration-150 group-hover:visible group-hover:opacity-100"
                      >
                        <span className="block text-sm font-normal italic leading-5 text-gray-200">
                          {statusTooltip.message}
                        </span>
                      </span>
                    )}
                  </span>
                </p>
              </div>

              {/* exp. del. date */}
              {rfqData?.providingDate?.length > 0 && <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal   lg:block">
                  Expected Delivery Date:
                </p>
                <p className=" font-bold  lg:block">
                  {rfqData?.providingDate
                    ? new Date(rfqData?.providingDate).toLocaleDateString("en-GB")
                    : "-"}
                </p>
              </div>}

              <div className="pt-2 lg:block">
                <p className="text-[#6B7280] font-normal">Contact Person:</p>

                {/* Mobile */}
                <div className="lg:hidden">
                  <p className="font-bold break-words">
                    {rfqData?.requesterName || "-"}
                    <span className="mx-2">|</span>
                    {requesterMobile ? (
                      <a
                        href={`tel:+91${requesterMobile}`}
                        className="text-[#004491] underline underline-offset-2"
                      >
                        +91-{rfqData?.requesterMobile}
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>

                {/* Desktop */}
                <p className="hidden lg:block font-bold">
                  {rfqData?.requesterName} | +91-{rfqData?.requesterMobile}
                </p>
              </div>

              {/* project name */}
              <div>
                {/* Mobile View - Single line with no indentation */}
                <div className="lg:hidden">
                  <span className="text-[#6B7280] font-normal whitespace-nowrap">
                    {hasMultipleProjects ? "Project Names: " : "Project Name: "}
                  </span>
                  <span className="font-bold">
                    {projects.map(project => project?.trim()).filter(Boolean).join(', ')}
                  </span>
                </div>

                {/* Desktop View - Stacked */}
                <div className="hidden lg:block">
                  <div className="text-[#6B7280] font-normal">
                    {hasMultipleProjects ? "Project Names:" : "Project Name:"}
                  </div>
                  <div className="mt-1">
                    {projects.map((project, index) => (
                      <div key={`project-${index}`} className="font-bold">
                        {project?.trim() || "-"}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <RfqSubProjectsSummary subProjects={rfqData?.subProjects} />
              {/* delivery location */}
              <div>
                {/* Mobile View - Single line with no indentation */}
                <div className="lg:hidden">
                  <span className="text-[#6B7280] font-normal whitespace-nowrap">
                    {hasMultipleLocations ? "Delivery Locations:" : "Delivery Location:"}
                  </span>
                  <span className="font-bold">
                    {locations.map((location, index) => (
                      <span key={`mobile-loc-${index}`} className="inline-flex items-center">

                        <img
                          src="https://storagereponeevaydevcdn.blob.core.windows.net/business/map_pin_vendor_quotation.svg"
                          alt=""
                          className="mx-1 w-3 h-3"
                        />

                        {location?.trim() || "-"}
                      </span>
                    ))}
                  </span>
                </div>

                {/* Desktop View - Stacked with icons */}
                <div className="hidden lg:block">
                  <div className="text-[#6B7280] font-normal">
                    {hasMultipleLocations ? "Delivery Locations:" : "Delivery Location:"}
                  </div>
                  <div className="mt-1">
                    {locations.map((location, index) => (
                      <div key={`location-${index}`} className="flex items-center gap-2">
                        <img
                          src="https://storagereponeevaydevcdn.blob.core.windows.net/business/map_pin_vendor_quotation.svg"
                          alt=""
                          className="mb-1"
                        />
                        <span className="font-bold">
                          {location?.trim() || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {firstQuotationSet?.submittedAt && (
                <div className="pt-2">
                  <p className="text-[#6B7280] font-normal">Submitted On:</p>

                  {/* Mobile */}
                  <div className="lg:hidden">
                    <p className="font-bold break-words">
                      {capitalizeFirstLetter(firstQuotationSet?.submittedBy)}
                      <span className="mx-2">|</span>
                      {uppercaseMeridiem(new Date(firstQuotationSet.submittedAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }))}
                    </p>
                  </div>

                  {/* Desktop */}
                  <p className="hidden lg:block font-bold">
                    {capitalizeFirstLetter(firstQuotationSet?.submittedBy)}
                    <span className="mx-2">|</span>
                    {uppercaseMeridiem(new Date(firstQuotationSet.submittedAt).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    }))}
                  </p>
                </div>
              )}

              {rfqData?.quotationDeadline && <div className="flex space-x-2 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal  lg:block">Quotation Deadline:</p>
                <p className=" font-bold  lg:block">
                  {(new Date(rfqData?.quotationDeadline).toLocaleDateString('en-GB'))}
                </p>
              </div>}

              {/* </div> */}
              {/* <div className="lg:space-y-5 space-y-4"> */}
              {/* <div>
              <p className="text-[#6B7280] font-normal">Status</p>
              <div className="flex items-center space-x-2">
                <div className="w-[15px] h-[15px] rounded-full bg-[#BFFFBA]"></div>
                <p className=" font-bold">{(rfqData?.status === "PENDING APPROVAL" || rfqData?.status === "CLOSED") ? "FREEZ" : rfqData?.status}
                </p>
                {(rfqData?.status === "PENDING APPROVAL" || rfqData?.status === "CLOSED") && <p className="gray-600 text-xs">Rates are frozen, contact to buyer.</p>}
              </div>
            </div> */}
              {/* </div> */}

              {(rfqData?.paymentTerms?.length > 0 || rfqData?.termsAndConditions?.length > 0) && (
                <div className="lg:col-span-3 lg:grid lg:grid-cols-2 lg:gap-4">
                  {rfqData?.paymentTerms?.length > 0 && (
                    <div className="rounded-xl border border-[#E7E7DD] bg-[#FCFCFA] p-4">
                      <p className="text-[#6B7280] font-normal">
                        Payment Terms:
                      </p>
                      <p className="mt-1 text-black">
                        {expandedStates[0]
                          ? stripHtml(rfqData.paymentTerms)
                          : `${stripHtml(rfqData.paymentTerms).substring(0, 200)}${rfqData.paymentTerms.length > 200 ? "..." : ""}`}
                        {rfqData?.paymentTerms.length > 200 && (
                          <button
                            type="button"
                            className="ml-1 text-[#6B7280] text-xs underline italic"
                            onClick={() => toggleExpanded(0)}
                          >
                            {expandedStates[0] ? "Read Less" : "Read More"}
                          </button>
                        )}
                      </p>
                    </div>
                  )}

                  {rfqData?.termsAndConditions?.length > 0 && (
                    <div className="mt-4 rounded-xl border border-[#E7E7DD] bg-[#FCFCFA] p-4 lg:mt-0">
                      <p className="text-[#6B7280] font-normal">
                        Terms & Conditions:
                      </p>
                      <div className="mt-1 font-light text-black">
                        {ReactHtmlParser(
                          expandedStates[1]
                            ? rfqData?.termsAndConditions
                            : rfqData?.termsAndConditions.substring(0, 200) +
                            (rfqData?.termsAndConditions.length > 200
                              ? "..."
                              : "")
                        )}
                      </div>
                      {rfqData?.termsAndConditions.length > 200 && (
                        <button
                          type="button"
                          className="text-xs underline italic"
                          onClick={() => toggleExpanded(1)}
                        >
                          {expandedStates[1] ? "Read Less" : "Read More"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* supporting doc */}
              <div className="lg:col-span-3 lg:space-x-0 lg:block">
                <p className="text-[#6B7280] font-normal lg:block pb-2">
                  RFQ Documents:
                </p>
                {attachmentsData.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {attachmentsData.map((doc, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center w-fit max-w-full bg-white border border-[#D9D9D9] rounded-full px-3 py-2"
                      >
                        <div className="flex min-w-0 items-center space-x-2">
                          <img
                            src={`/assets/icons/${doc.Path}.png`}
                            alt={doc.Path}
                            className="h-5 w-5 flex-shrink-0"
                          />
                          <a
                            href={doc.Content}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={doc.Name}
                            className="text-xs text-[#434343] hover:underline"
                            title={doc.Name}
                          >
                            {doc.Name}
                          </a>
                        </div>

                        <a
                          href={doc.Content}
                          download={doc.Name}
                          className="block lg:hidden ml-2"
                        >
                          <img
                            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/download_solid.svg"
                            alt="Download"
                            className="h-4 w-4"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="italic">No Supporting Documents provided by Buyer </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {quotationUpdateTrail.length > 0 && (
        <div className="quotation-updation-trail-card mt-4 bg-white border rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setIsQuotationTrailOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-b-gray-200 hover:bg-[#FBFBF7]"
          >
            <div className="flex flex-wrap items-center gap-2 text-left">
              <h3 className="text-base font-semibold">
                Quotation Updation Trail
              </h3>
              <span className="rounded-full bg-[#FFF0E4] px-3 py-1 text-xs font-semibold text-[#E95420]">
                {Math.max(quotationUpdateTrail.length - 1, 0)}{" "}
                {quotationUpdateTrail.length - 1 === 1 ? "revision" : "revisions"}
              </span>
            </div>

            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isQuotationTrailOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isQuotationTrailOpen && (
            <div className="p-4">
              <p className="mb-3 text-xs text-[#6B7280]">
                Track who submitted or edited the quotation form.
              </p>

              <div className="overflow-x-auto rounded-lg border border-[#E7E7DD] bg-white">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-[#F8F8F3]">
                      <tr className="border-b border-[#E7E7DD] text-[#6B7280]">
                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Submitted By</th>
                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Day & Date</th>
                        <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Difference</th>
                        <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Quotation Amount</th>
                        <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationUpdateTrail.map(({ quotationSet, quotationIndex }, index) => {
                      const submittedDateParts = getQuotationSubmittedDateParts(quotationSet?.submittedAt);
                      const submittedBy = capitalizeFirstLetter(quotationSet?.submittedBy) || "-";
                      const quotationAmount = getQuotationAmount(quotationSet);
                      const previousQuotationAmount = index > 0
                        ? getQuotationAmount(quotationUpdateTrail[index - 1].quotationSet)
                        : 0;
                      const differenceAmount = index > 0 ? quotationAmount - previousQuotationAmount : 0;
                      return (
                        <tr
                          key={`${quotationSet?.submittedAt || "submitted"}-${index}`}
                          className="bg-white border-b border-[#E7E7DD] last:border-b-0"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E4E7FF] text-[11px] font-bold text-[#1F2A44]">
                                {submittedBy !== "-" ? submittedBy.charAt(0) : "-"}
                              </span>
                              <div>
                                <p className="font-semibold text-[#111827]">{submittedBy}</p>
                                <p className="text-[11px] text-[#6B7280]">
                                  {quotationIndex === 0 ? "R0 Quote" : `R${quotationIndex}`}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium text-[#374151] whitespace-nowrap">
                            {submittedDateParts.day !== "-" || submittedDateParts.date !== "-"
                              ? `${submittedDateParts.day}, ${submittedDateParts.date} | ${submittedDateParts.time}`
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-[#6B7280] whitespace-nowrap">
                            {index > 0 && differenceAmount
                              ? formatDifference(differenceAmount, previousQuotationAmount)
                              : "--"}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openQuotationVersion(quotationIndex)}
                              className="font-semibold text-[#111827] hover:underline"
                              title={`Open R${quotationIndex} quotation form`}
                            >
                              {formatAmount(quotationAmount)}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <QuotationTrailPdfButton
                                action="preview"
                                rfqData={rfqData}
                                quotationSet={quotationSet}
                                quotationIndex={quotationIndex}
                                quotationHistory={quotationUpdateTrail}
                              />
                              <QuotationTrailPdfButton
                                rfqData={rfqData}
                                quotationSet={quotationSet}
                                quotationIndex={quotationIndex}
                                quotationHistory={quotationUpdateTrail}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RfqInformation;
